import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ModalLayout from '../layouts/ModalLayout'
import ServiceDetail from './ServiceDetailModal'
import { modalPrimaryButtonStyle } from '../styles/buttonStyles'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import TableSortLabel from '@mui/material/TableSortLabel'
import ResetTableButton from '../components/ResetTableButton'
import GenericListFilter from '../components/GenericListFilter'
import FilterToggleButton from '../components/FilterToggleButton'
import GenericSearchToggle from '../components/GenericSearchToggle'
import useGenericTextSearch from '../hooks/useGenericTextSearch'
import useSharedModalUiState from '../hooks/useSharedModalUiState'
import ExportButton from '../components/ExportButton'
import { useAuth } from '../context/AuthContext'

import {
  buildServiceExportRecords,
  exportServicesCSV,
  exportServicesXLS,
  exportServicesPDF,
  exportServicesXML,
  exportServicesJSON
} from '../utils/serviceExport'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const serviceDetailFields = [
  ['date', 'TRIPLOG.SERVICECOSTS.TABLE.date'],
  ['vehicleId', 'TRIPLOG.SERVICECOSTS.TABLE.vehicleId'],
  ['spz', 'vehicle_license_plate'],
  ['serviceType', 'TRIPLOG.SERVICECOSTS.TABLE.serviceType'],
  ['kilometers', 'TRIPLOG.SERVICECOSTS.TABLE.kilometers'],
  ['description', 'TRIPLOG.SERVICECOSTS.TABLE.description'],
  ['laborCost', 'TRIPLOG.SERVICECOSTS.TABLE.laborCost'],
  ['materialCost', 'TRIPLOG.SERVICECOSTS.TABLE.materialCost'],
  ['totalCost', 'TRIPLOG.SERVICECOSTS.TABLE.totalCost'],
  ['workshopName', 'TRIPLOG.SERVICECOSTS.TABLE.workshopName'],
  ['workshopAddress', 'TRIPLOG.SERVICECOSTS.TABLE.workshopAddress'],
  ['documentNumber', 'TRIPLOG.SERVICECOSTS.TABLE.documentNumber'],
  ['note', 'TRIPLOG.SERVICECOSTS.TABLE.note'],
]

const SERVICE_MODAL_COLUMNS_KEY = 'serviceModalColumns'
const SERVICE_MODAL_COL_WIDTHS_KEY = 'serviceModalColWidths'

function SortableHeaderCell({ col, orderBy, order, onSort, colWidths, onResize }) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: col.key })
  const customWidth = colWidths[col.key]

  return (
    <th
      ref={setNodeRef}
      style={{
        ...thStyle,
        transform: CSS.Transform.toString(transform),
        transition,
        ...(customWidth ? { width: customWidth, minWidth: customWidth, maxWidth: customWidth } : { minWidth: 120 }),
        position: 'relative',
        paddingRight: 8,
        boxSizing: 'border-box'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span {...attributes} {...listeners} style={{ cursor: 'grab', marginRight: 6, color: '#999' }}>⠿</span>
        <TableSortLabel
          active={orderBy === col.key}
          direction={orderBy === col.key ? order : 'asc'}
          onClick={() => onSort(col.key)}
        >
          {t(col.labelKey)}
  {(col.key === 'laborCost' || col.key === 'materialCost' || col.key === 'totalCost') && ' (€)'}
        </TableSortLabel>
      </div>
      <div style={{ position: 'absolute', right: 4, top: '20%', bottom: '20%', width: 1, background: '#9e9e9e' }} />
      <div onMouseDown={(e) => onResize(e, col.key)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'col-resize' }} />
    </th>
  )
}

const ServiceListModal = ({
  open,
  onClose,
  services = [],
  servicesLoading,
  servicesError,
  filterCriteria,
  setFilterCriteria,
  openExportDropdown
}) => {
  const { t } = useTranslation()

const { user } = useAuth()
const isLoggedIn = Boolean(user)
const membershipId = user?.membership_type_id ?? null
const limitedMembership = membershipId === 1 || membershipId === 4
const fullMembership = !limitedMembership

  const [viewport, setViewport] = useState(window.innerWidth)
  const [modalSize, setModalSize] = useState('fullscreen')
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [vehicleDetailOpen, setVehicleDetailOpen] = useState(false)
  const [hoveredVehicle, setHoveredVehicle] = useState(null)
  const [activeRow, setActiveRow] = useState(null)
  const [vehiclesById, setVehiclesById] = useState({})
const [tooltipEnabled, setTooltipEnabled] = useState(() => {
  const stored = localStorage.getItem('tooltipEnabled')
  return stored === null ? true : stored === 'true'
})

useEffect(() => {
  localStorage.setItem('tooltipEnabled', tooltipEnabled)
}, [tooltipEnabled])

const [pressedBtn, setPressedBtn] = useState(null)
const [hoverBtn, setHoverBtn] = useState(null)

const { showSearch, setShowSearch, showFilter, setShowFilter } = useSharedModalUiState()
const [searchQuery, setSearchQuery] = useState('')

const [forceOpenExport, setForceOpenExport] = useState(false)

useEffect(() => {
  if (openExportDropdown) {
    setForceOpenExport(true)

    const timer = setTimeout(() => {
      setForceOpenExport(false)
    }, 50)

    return () => clearTimeout(timer)
  }
}, [openExportDropdown])

const [orderBy, setOrderBy] = useState(() => localStorage.getItem('serviceOrderBy') || 'date')
const [order, setOrder] = useState(() => localStorage.getItem('serviceOrder') || 'desc')
const [columns, setColumns] = useState(() => {
  const stored = localStorage.getItem(SERVICE_MODAL_COLUMNS_KEY)
  if (stored) return JSON.parse(stored)

  return serviceDetailFields.map(([key, labelKey]) => ({
    key,
    labelKey
  }))
})
const [colWidths, setColWidths] = useState(() => {
  const stored = localStorage.getItem(SERVICE_MODAL_COL_WIDTHS_KEY)
  return stored ? JSON.parse(stored) : {}
})
const sensors = useSensors(useSensor(PointerSensor))

  useEffect(() => {
    localStorage.setItem(SERVICE_MODAL_COLUMNS_KEY, JSON.stringify(columns))
  }, [columns])

  useEffect(() => {
    localStorage.setItem(SERVICE_MODAL_COL_WIDTHS_KEY, JSON.stringify(colWidths))
  }, [colWidths])

  useEffect(() => {
    localStorage.setItem('serviceOrderBy', orderBy)
  }, [orderBy])

  useEffect(() => {
    localStorage.setItem('serviceOrder', order)
  }, [order])

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(col)
      setOrder('asc')
    }
  }

  const handleMouseDown = (e, key) => {
    const startX = e.clientX
    const startWidth = colWidths[key] || 150

    const onMouseMove = (event) => {
      const delta = event.clientX - startX
      const newWidth = Math.max(80, startWidth + delta)
      setColWidths(prev => ({ ...prev, [key]: newWidth }))
    }

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = columns.findIndex(c => c.key === active.id)
    const newIndex = columns.findIndex(c => c.key === over.id)
    const reordered = [...columns]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)
    setColumns(reordered)
  }

  useEffect(() => {
    const handleResize = () => setViewport(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (open) setModalSize('fullscreen')
  }, [open])

  useEffect(() => {
    const loadVehicles = async () => {
      if (!services.length) return

      const userId = services[0]?.userId
      if (!userId) return

      try {
        const res = await fetchWithAuth(`/users/${userId}/vehicles`)
        if (!res.ok) return

        const data = await res.json()
        if (!Array.isArray(data)) return

        const index = data.reduce((acc, vehicle) => {
          if (vehicle?.id == null) return acc
          acc[String(vehicle.id)] = vehicle
          return acc
        }, {})

        setVehiclesById(index)
      } catch {}
    }

    loadVehicles()
  }, [services])

  const getVehicleLabel = (s) => {
    const vehicleFromList = vehiclesById[String(s?.vehicleId)]
    const vehicle = vehicleFromList || s.vehicle

    return (
      vehicle?.name ||
      vehicle?.model ||
      vehicle?.licensePlate ||
      vehicle?.spz ||
      '-'
    )
  }

const getVehicleSpz = (s) => {
  const vehicleFromList = vehiclesById[String(s?.vehicleId)]
  const vehicle = vehicleFromList || s.vehicle

  return vehicle?.licensePlate || vehicle?.spz || '-'
}

const getServiceSearchData = (service) => {
  const vehicleFromList = vehiclesById[String(service?.vehicleId)]
  const vehicle = vehicleFromList || service?.vehicle

  return {
    ...service,
    vehicleName: vehicle?.name,
    vehicleModel: vehicle?.model,
    vehicleLicensePlate: vehicle?.licensePlate,
    vehicleSpz: vehicle?.spz,
    searchDate: service?.date
      ? new Date(service.date).toLocaleDateString('sk-SK')
      : ''
  }
}

const filteredServices = services.filter((service) => {
  const itemDate = service?.date ? service.date.slice(0, 10) : null

  if (filterCriteria?.vehicleId && String(service.vehicleId) !== String(filterCriteria.vehicleId)) return false
  if (filterCriteria?.from && itemDate < filterCriteria.from) return false
  if (filterCriteria?.to && itemDate > filterCriteria.to) return false
  if (filterCriteria?.month && !itemDate?.startsWith(filterCriteria.month)) return false
  if (filterCriteria?.year && !itemDate?.startsWith(filterCriteria.year)) return false

  return true
})

const searchFilteredServices = useGenericTextSearch(
  filteredServices,
  showSearch ? searchQuery : '',
  getServiceSearchData
)

  const sortedServices = [...searchFilteredServices].sort((a, b) => {
    let aVal = a[orderBy]
    let bVal = b[orderBy]

    if (orderBy === 'vehicleId') {
      aVal = getVehicleLabel(a)
      bVal = getVehicleLabel(b)
    }

if (orderBy === 'spz') {
  aVal = getVehicleSpz(a)
  bVal = getVehicleSpz(b)
}

    if (orderBy === 'date') {
      aVal = aVal ? new Date(aVal).getTime() : 0
      bVal = bVal ? new Date(bVal).getTime() : 0
    }

    if (['kilometers', 'laborCost', 'materialCost', 'totalCost'].includes(orderBy)) {
      aVal = Number(aVal) || 0
      bVal = Number(bVal) || 0
    }

    if (typeof aVal === 'string') aVal = aVal.toLowerCase()
    if (typeof bVal === 'string') bVal = bVal.toLowerCase()

    if (aVal > bVal) return order === 'asc' ? 1 : -1
    if (aVal < bVal) return order === 'asc' ? -1 : 1
    return 0
  })

const exportRecords = buildServiceExportRecords(
  sortedServices,
  vehiclesById
)

const exportHeaders = columns.map(c => c.key)

const selectedServiceIndex = sortedServices.findIndex(
  (item) => item?.id === selectedVehicle?.id
)

const goToPrevService = () => {
  if (selectedServiceIndex <= 0) return
  const prevService = sortedServices[selectedServiceIndex - 1]
  setSelectedVehicle(prevService)
  setActiveRow(prevService?.id ?? null)
}

const goToNextService = () => {
  if (selectedServiceIndex < 0 || selectedServiceIndex >= sortedServices.length - 1) return
  const nextService = sortedServices[selectedServiceIndex + 1]
  setSelectedVehicle(nextService)
  setActiveRow(nextService?.id ?? null)
}

let periodText = t('TRIPLOG.STATISTICS.ALL_TIME')

if (filterCriteria?.month) {
  const [year, month] = filterCriteria.month.split('-')
  periodText = `${month}-${year}`
} else if (filterCriteria?.year) {
  periodText = filterCriteria.year
} else if (filterCriteria?.from || filterCriteria?.to) {
  const from = filterCriteria.from || ''
  const to = filterCriteria.to || ''
  periodText = `${from} - ${to}`
}

let vehicleText = t('TRIPLOG.BUTTON.ALL_VEHICLES')

if (filterCriteria?.vehicleId) {
  const v = vehiclesById[String(filterCriteria.vehicleId)]

  if (v) {
    const name = v.name || v.model || ''
    const plate = v.licensePlate || v.spz || ''

    vehicleText = plate ? `${name} (${plate})` : name
  }
}

  if (!open) return null

  return (
    <>
      <ModalLayout open={open} onClose={onClose} size={modalSize} closeOnEscape={!vehicleDetailOpen}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>

<div
  style={{
    position: 'absolute',
    top: -10,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }}
>

<GenericSearchToggle
  visible={showSearch}
  query={searchQuery}
  onQueryChange={setSearchQuery}
  onToggle={() => {
    setShowSearch(prev => {
      if (prev) setSearchQuery('')
      return !prev
    })
  }}
  placeholder={t('TRIPLOG.SEARCH_PLACEHOLDER', { defaultValue: 'Hľadať...' })}
/>

<FilterToggleButton
  visible={showFilter}
  active={Object.values(filterCriteria || {}).some(v => v && v !== '')}
  onToggle={() => setShowFilter(prev => !prev)}
/>

<label
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    fontSize: 13,
    userSelect: 'none',
    marginTop: 5
  }}
>
              <input
                type="checkbox"
                checked={tooltipEnabled}
                onChange={(e) => setTooltipEnabled(e.target.checked)}
                style={{
                  cursor: 'pointer',
                  appearance: 'none',
                  width: 16,
                  height: 16,
                  border: '1px solid #1e3f44',
                  borderRadius: 3,
                  background: tooltipEnabled ? '#06857a' : '#fff'
                }}
              />
              {t('TRIPLOG.SERVICECOSTS.TOGGLE')}
            </label>
<ResetTableButton
  isModified={
    Object.keys(colWidths).length > 0 ||
    JSON.stringify(columns.map(c => c.key)) !==
      JSON.stringify(serviceDetailFields.map(([key]) => key)) ||
    orderBy !== 'date' ||
    order !== 'desc'
  }
  onReset={() => {
const resetCols = serviceDetailFields.map(([key, labelKey]) => ({
  key,
  labelKey
}))

    setColumns(resetCols)
    setColWidths({})
    setOrderBy('date')
    setOrder('desc')

    localStorage.removeItem(SERVICE_MODAL_COLUMNS_KEY)
    localStorage.removeItem(SERVICE_MODAL_COL_WIDTHS_KEY)
    localStorage.removeItem('serviceOrderBy')
    localStorage.removeItem('serviceOrder')
  }}
/>
<ExportButton
  onCSV={() => exportServicesCSV(exportRecords, exportHeaders, t, periodText, vehicleText)}
  onXLS={() => exportServicesXLS(exportRecords, exportHeaders, t, periodText, vehicleText)}
  onPDF={() => exportServicesPDF(exportRecords, exportHeaders, t, periodText, vehicleText)}
  onXML={() => exportServicesXML(exportRecords, exportHeaders)}
  onJSON={() => exportServicesJSON(exportRecords)}
  isLoggedIn={isLoggedIn}
  canUsePremiumFormats={isLoggedIn && fullMembership}
  forceOpen={forceOpenExport}
/>

            <button
              onClick={() =>
                setModalSize(prev => prev === 'fullscreen' ? 'max' : 'fullscreen')
              }
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 32
              }}
            >
              ⛶
            </button>
          </div>

          <h3 style={{ fontSize: 20, marginTop: 0, marginBottom: 10 }}>
            {t('TRIPLOG.TAB.SERVIS')}
          </h3>
{showFilter && (
  <GenericListFilter
    value={filterCriteria}
    onChange={setFilterCriteria}
vehicles={Object.values(vehiclesById).map(v => {
  const name = v.name || v.model || v.licensePlate || v.spz || `Vozidlo #${v.id}`
  const plate = v.licensePlate || v.spz || ''

  return {
    value: String(v.id),
    label: plate && name !== plate ? `${name} (${plate})` : name
  }
})}
    labels={{
      allVehicles: t('TRIPLOG.BUTTON.ALL_VEHICLES'),
      from: t('TRIPLOG.FILTER_FROM'),
      to: t('TRIPLOG.FILTER_TO'),
      month: t('TRIPLOG.FILTER_MONTH'),
      year: t('TRIPLOG.FILTER_YEAR'),
      clear: t('TRIPLOG.BUTTON.CLEAR')
    }}
  />
)}
          {servicesLoading && <div>{t('LOADING')}</div>}
          {servicesError && <div style={{ color: 'red' }}>{servicesError}</div>}

<div
  style={{
    border: '1px solid #c7c7c7',
    borderRadius: 10,
    background: '#fff',
    flex: 1,
    overflow: 'auto',
    minHeight: 0
  }}
>
            <table
              style={{
                borderCollapse: 'collapse',
                width: 'max-content',
                minWidth: '100%',
                fontSize: 12,
                whiteSpace: 'nowrap'
              }}
            >
              <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                <tr style={{ background: '#c8e1e1', fontWeight: 700 }}>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={columns.map(c => c.key)} strategy={horizontalListSortingStrategy}>
                      {columns.map((col) => (
                        <SortableHeaderCell
                          key={col.key}
                          col={col}
                          orderBy={orderBy}
                          order={order}
                          onSort={handleSort}
                          colWidths={colWidths}
                          onResize={handleMouseDown}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                  </tr>
              </thead>

              <tbody>
                {services.length === 0 ? (
                  <tr>
                    <td colSpan={serviceDetailFields.length + 1} style={{ padding: 16 }}>
                      {t('TRIPLOG.SERVICECOSTS.EMPTY')}
                    </td>
                  </tr>
                ) : (
                  sortedServices.map(s => (
		<tr
 		 key={s.id}
 		 style={{
 		   borderTop: '1px solid #ececec',
  		  backgroundColor:
  		    activeRow === s.id
   		     ? '#e6f4f1'
    		    : hoveredVehicle?.id === s.id
    		      ? '#f3fafa'
     		     : '#fff',
  		  cursor: 'pointer'
 		 }}
 		 onClick={() => {
 		   setActiveRow(s.id)
 		   setSelectedVehicle(s)
 		   setVehicleDetailOpen(true)
  		}}
 		 onMouseEnter={(e) => {
 		   if (e.target.closest('.vehicle-detail-button')) return
 		   setHoveredVehicle(s)
 		 }}
 		 onMouseMove={(e) => {
 		   if (e.target.closest('.vehicle-detail-button')) {
  		    setHoveredVehicle(null)
  		  } else {
 		     setHoveredVehicle(s)
 		   }
 		 }}
		  onMouseLeave={(e) => {
   		 const related = e.relatedTarget
  		  if (related && related.closest && related.closest('.vehicle-tooltip')) return
  		  setHoveredVehicle(null)
  		}}
		>
		{serviceDetailFields.map(([key]) => (
 		 <td key={key} style={tdStyle}>
{key === 'vehicleId'
  ? getVehicleLabel(s)
  : key === 'spz'
    ? getVehicleSpz(s)
    : key === 'date'
      ? (s.date
          ? new Date(s.date).toLocaleDateString('sk-SK')
          : '-')
      : ['laborCost', 'materialCost', 'totalCost'].includes(key)
        ? (s[key] != null ? Number(s[key]).toFixed(2) : '-')
        : s[key] ?? '-'}
  		</td>
		))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {tooltipEnabled && hoveredVehicle && (
            <div
              className="vehicle-tooltip"
              onMouseLeave={() => setHoveredVehicle(null)}
              style={{
                position: 'fixed',
                right:
                  viewport >= 2400 ? 69 :
                  viewport >= 1920 ? 51 :
                  viewport >= 1600 ? 40 :
                  viewport >= 1366 ? 38 : 20,
                bottom:
                  viewport >= 1920 ? 120 :
                  viewport >= 1600 ? 110 :
                  viewport >= 1366 ? 105 : 30,
                fontSize:
                  viewport >= 1920 ? 14 :
                  viewport >= 1600 ? 14 :
                  viewport >= 1366 ? 13 : 13,
                maxHeight:
                  viewport >= 1920 ? '80vh' :
                  viewport >= 1600 ? '75vh' : '96vh',
                background: '#f3fafa',
                border: '1px solid #1e3f44',
                borderRadius: 8,
                padding: '16px 20px',
                boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
                zIndex: 9999,
                overflowY: 'auto'
              }}
            >
              {serviceDetailFields.map(([key, labelKey]) => (
                <div key={key} style={{ marginBottom: 6, display: 'flex' }}>
<strong style={{ minWidth: 200 }}>
  {['laborCost', 'materialCost', 'totalCost'].includes(key)
    ? `${t(labelKey)} (€):`
    : `${t(labelKey)}:`}
</strong>
<span style={{ color: '#1e3f44', fontWeight: 600 }}>
{key === 'vehicleId'
  ? getVehicleLabel(hoveredVehicle)
  : key === 'spz'
    ? getVehicleSpz(hoveredVehicle)
    : key === 'date'
      ? (hoveredVehicle.date
          ? new Date(hoveredVehicle.date).toLocaleDateString('sk-SK')
          : '-')
      : ['laborCost', 'materialCost', 'totalCost'].includes(key)
        ? (hoveredVehicle[key] != null
            ? Number(hoveredVehicle[key]).toFixed(2)
            : '-')
        : hoveredVehicle[key] ?? '-'}
</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 15, display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={onClose}
              onMouseEnter={() => setHoverBtn('close')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('close')}
              onMouseUp={() => setPressedBtn(null)}
              style={modalPrimaryButtonStyle(
                pressedBtn,
                'close',
                hoverBtn === 'close'
              )}
            >
              {t('common.close')}
            </button>
          </div>

        </div>
      </ModalLayout>

      <ServiceDetail
        open={vehicleDetailOpen}
        onClose={() => {
          setVehicleDetailOpen(false)
          setSelectedVehicle(null)
          setActiveRow(null)
        }}
        service={selectedVehicle}
        onPrevService={goToPrevService}
        onNextService={goToNextService}
        hasPrevService={selectedServiceIndex > 0}
        hasNextService={selectedServiceIndex >= 0 && selectedServiceIndex < sortedServices.length - 1}
      />
    </>
  )
}

const thStyle = {
  padding: '8px 12px',
  textAlign: 'left',
  borderBottom: '1px solid #c7c7c7'
}

const tdStyle = {
  padding: '8px 12px'
}

export default ServiceListModal